import React, { useState } from "react";
import { Link } from "react-router-dom";
import {ReactComponent as AllBook} from 'src/assets/author/allbook_author.svg';
import { motion } from "framer-motion";

interface Book {
    bookTitle: string;
    coverImage: string;
    bookId?: string;
    bookIntro?: string;
    bookDescription?: string;
    likeCount?: number;
    viewCount?: number;
    keywords?: string[];
}

interface BookProps {
    bookData: Book[];
}

const BookListMobile = ({ bookData }: BookProps) => {
    const INITIAL_COUNT = 3;
    const [showAll, setShowAll] = useState(false);

    if (bookData.length === 0) return null;

    const firstBook = bookData[0] as any;
    const restBooks = bookData.slice(1);
    const displayedRest = showAll ? restBooks : restBooks.slice(0, INITIAL_COUNT);

    const coverSrc = (book: any) =>
        book.coverImage
            ? `data:image/jpeg;base64,${book.coverImage}`
            : `${process.env.PUBLIC_URL}/img/book_5.jpg`;

    return (
        <div className="flex flex-col gap-4 px-4 pb-6">
            {/* Section title */}
            <div className="flex items-center gap-2">
                <AllBook/>
                <span className="text-[#101828] text-xl font-bold">모든 작품</span>
                <span className="text-[#19cf7a] text-lg font-semibold">{bookData.length}</span>
            </div>

            {/* First book — large featured card */}
            <motion.div 
                initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-50px" }} transition={{ duration: 0.6 }}
                className="bg-white border border-[#f3f4f6] rounded-3xl overflow-hidden shadow-[0px_8px_30px_-10px_rgba(25,207,122,0.2)]">
                {/* Cover image with gradient overlay */}
                <Link to={`/book/bookdetails/${firstBook.bookId}`} className="block relative">
                    <div className="w-full h-[220px] relative">
                        <img
                            className="w-full h-full object-cover"
                            src={coverSrc(firstBook)}
                            alt={firstBook.bookTitle}
                        />
                        {/* gradient overlay */}
                        <div className="absolute inset-0 bg-gradient-to-t from-[rgba(16,24,40,0.6)] via-transparent to-transparent" />
                        
                        {/* tags */}
                        {firstBook.keywords && firstBook.keywords.length > 0 && (
                            <div className="absolute bottom-4 left-4 flex flex-wrap gap-1.5">
                                {firstBook.keywords.map((kw: string, i: number) => (
                                    <span
                                        key={i}
                                        className="bg-[rgba(0,0,0,0.3)] border border-[rgba(255,255,255,0.2)] text-white text-xs font-semibold rounded-full px-3 py-1.5 shadow-[0px_1px_3px_0px_rgba(0,0,0,0.1)]"
                                    >
                                        #{kw}
                                    </span>
                                ))}
                            </div>
                        )}
                    </div>
                </Link>
                {/* Book info */}
                <div className="px-6 py-5 flex flex-col gap-3">
                    <div className="flex flex-col gap-2">
                        <h3 className="text-[#101828] text-[22px] font-extrabold leading-snug break-words">{firstBook.bookTitle}</h3>
                        {firstBook.bookDescription && (
                            <p className="text-[#4a5565] text-[15px] leading-relaxed line-clamp-2 break-words">
                                {firstBook.bookDescription}
                            </p>
                        )}
                    </div>

                    <div className="flex flex-row items-center gap-4 text-[#4a5565] text-[15px] font-medium my-1">
                        <div className="flex flex-row gap-1.5 items-center">
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                                <path d="M12.6626 9.33105C13.6557 8.35796 14.6621 7.19158 14.6621 5.66528C14.6621 4.69306 14.2759 3.76066 13.5884 3.07319C12.901 2.38573 11.9686 1.99951 10.9963 1.99951C9.82329 1.99951 8.99683 2.33276 7.99707 3.33252C6.99731 2.33276 6.17085 1.99951 4.9978 1.99951C4.02558 1.99951 3.09318 2.38573 2.40571 3.07319C1.71825 3.76066 1.33203 4.69306 1.33203 5.66528C1.33203 7.19824 2.33179 8.36462 3.33154 9.33105L7.99707 13.9966L12.6626 9.33105Z" stroke="#19CF7A" strokeWidth="1.33301" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                            {firstBook.likeCount ?? 0}
                        </div>
                        <div className="flex flex-row gap-1.5 items-center">
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                                <path d="M1.37369 8.23007C1.31814 8.08043 1.31814 7.91582 1.37369 7.76618C1.91469 6.4544 2.83301 5.3328 4.01223 4.54357C5.19144 3.75433 6.57845 3.33301 7.99741 3.33301C9.41636 3.33301 10.8034 3.75433 11.9826 4.54357C13.1618 5.3328 14.0801 6.4544 14.6211 7.76618C14.6767 7.91582 14.6767 8.08043 14.6211 8.23007C14.0801 9.54184 13.1618 10.6634 11.9826 11.4527C10.8034 12.2419 9.41636 12.6632 7.99741 12.6632C6.57845 12.6632 5.19144 12.2419 4.01223 11.4527C2.83301 10.6634 1.91469 9.54184 1.37369 8.23007Z" stroke="#19CF7A" strokeWidth="1.33301" strokeLinecap="round" strokeLinejoin="round"/>
                                <path d="M7.99756 9.99756C9.10186 9.99756 9.99707 9.10235 9.99707 7.99805C9.99707 6.89375 9.10186 5.99854 7.99756 5.99854C6.89326 5.99854 5.99805 6.89375 5.99805 7.99805C5.99805 9.10235 6.89326 9.99756 7.99756 9.99756Z" stroke="#19CF7A" strokeWidth="1.33301" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                            {firstBook.viewCount ?? 0}
                        </div>
                    </div>

                    <Link
                        to={`/book/bookdetails/${firstBook.bookId}`}
                        className="w-full h-[52px] mt-1 flex items-center justify-center bg-[rgba(25,207,122,0.1)] text-[#00c896] text-base font-bold rounded-2xl"
                    >
                        자세히 보기
                    </Link>
                </div>
            </motion.div>

            {/* Other books — horizontal cards */}
            {restBooks.length > 0 && (
                <div className="flex flex-col gap-4">
                    {displayedRest.map((book: any, idx: number) => (
                        <motion.div 
                            key={idx} 
                            initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-50px" }} transition={{ duration: 0.6, delay: (idx % 3) * 0.1 }}
                            className="bg-white border border-[#f3f4f6] rounded-3xl shadow-[0px_2px_8px_0px_rgba(0,0,0,0.04)] p-4 flex flex-row gap-4 items-stretch">
                            {/* Small cover */}
                            <Link to={`/book/bookdetails/${book.bookId}`} className="flex-shrink-0 block">
                                <div className="w-[88px] h-[116px] rounded-2xl overflow-hidden bg-[#f3f4f6]">
                                    <img
                                        className="w-full h-full object-cover"
                                        src={coverSrc(book)}
                                        alt={book.bookTitle}
                                    />
                                </div>
                            </Link>

                            {/* Info */}
                            <div className="flex-1 flex flex-col justify-between min-w-0">
                                <div className="flex flex-col gap-1">
                                    <h4 className="text-[#101828] text-base font-bold leading-snug line-clamp-1 break-words">{book.bookTitle}</h4>
                                    {book.bookDescription && (
                                        <p className="text-[#6a7282] text-sm leading-relaxed line-clamp-2 break-words">
                                            {book.bookDescription}
                                        </p>
                                    )}
                                </div>

                                <div className="flex flex-row items-center justify-between mt-2">
                                    <div className="flex flex-row items-center gap-3">
                                        <div className="flex flex-row gap-1 items-center text-[#6a7282] text-sm font-medium">
                                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 16 16" fill="none">
                                                <path d="M12.6626 9.33105C13.6557 8.35796 14.6621 7.19158 14.6621 5.66528C14.6621 4.69306 14.2759 3.76066 13.5884 3.07319C12.901 2.38573 11.9686 1.99951 10.9963 1.99951C9.82329 1.99951 8.99683 2.33276 7.99707 3.33252C6.99731 2.33276 6.17085 1.99951 4.9978 1.99951C4.02558 1.99951 3.09318 2.38573 2.40571 3.07319C1.71825 3.76066 1.33203 4.69306 1.33203 5.66528C1.33203 7.19824 2.33179 8.36462 3.33154 9.33105L7.99707 13.9966L12.6626 9.33105Z" stroke="#99A1AF" strokeWidth="1.33301" strokeLinecap="round" strokeLinejoin="round"/>
                                            </svg>
                                            {book.likeCount ?? 0}
                                        </div>
                                        <div className="flex flex-row gap-1 items-center text-[#6a7282] text-sm font-medium">
                                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 16 16" fill="none">
                                                <path d="M1.37369 8.23007C1.31814 8.08043 1.31814 7.91582 1.37369 7.76618C1.91469 6.4544 2.83301 5.3328 4.01223 4.54357C5.19144 3.75433 6.57845 3.33301 7.99741 3.33301C9.41636 3.33301 10.8034 3.75433 11.9826 4.54357C13.1618 5.3328 14.0801 6.4544 14.6211 7.76618C14.6767 7.91582 14.6767 8.08043 14.6211 8.23007C14.0801 9.54184 13.1618 10.6634 11.9826 11.4527C10.8034 12.2419 9.41636 12.6632 7.99741 12.6632C6.57845 12.6632 5.19144 12.2419 4.01223 11.4527C2.83301 10.6634 1.91469 9.54184 1.37369 8.23007Z" stroke="#99A1AF" strokeWidth="1.33301" strokeLinecap="round" strokeLinejoin="round"/>
                                                <path d="M7.99756 9.99756C9.10186 9.99756 9.99707 9.10235 9.99707 7.99805C9.99707 6.89375 9.10186 5.99854 7.99756 5.99854C6.89326 5.99854 5.99805 6.89375 5.99805 7.99805C5.99805 9.10235 6.89326 9.99756 7.99756 9.99756Z" stroke="#99A1AF" strokeWidth="1.33301" strokeLinecap="round" strokeLinejoin="round"/>
                                            </svg>
                                            {book.viewCount ?? 0}
                                        </div>
                                    </div>
                                    <Link
                                        to={`/book/bookdetails/${book.bookId}`}
                                        className="bg-[rgba(25,207,122,0.1)] text-[#00c896] text-xs font-bold rounded-xl px-4 py-2 hover:bg-[#19cf7a] hover:text-white transition-colors"
                                    >
                                        보기
                                    </Link>
                                </div>
                            </div>
                        </motion.div>
                    ))}
                </div>
            )}

            {/* 더보기 button */}
            {!showAll && restBooks.length > INITIAL_COUNT && (
                <button
                    onClick={() => setShowAll(true)}
                    className="w-full h-12 border border-[#e5e7eb] text-[#4a5565] text-base font-semibold rounded-2xl hover:border-[#19cf7a] hover:text-[#19cf7a] transition-colors"
                >
                    작품 더보기
                </button>
            )}
        </div>
    );
};

export default BookListMobile;
